import { Navigate } from "react-router-dom";
import { useRouteGate } from "@/hooks/useRouteGate";
import { useLocationSession } from "@/modules/location-session/hooks/useLocationSession";
import { useRestrictedStaff } from "@/hooks/useRestrictedStaff";
import { useCurrentUserStaff } from "@/hooks/useCurrentUserStaff";
import { PageLoader } from "@/components/ui/page-loader";
import { isLocationAllowedRoutePath, LOCATION_VIEWER_HOME_PATH } from "@/routes/locationAllowlist";
import { isRestrictedAllowedRoutePath, RESTRICTED_STAFF_HOME_PATH } from "@/routes/restrictedAllowlist";
import { ACCOUNTANT_HOME_PATH, isAccountantAllowedRoutePath } from "@/routes/accountantAllowlist";
import type { RouteGate } from "@/routes/types";

/**
 * Central route-gate enforcement (docs/plans/2026-07-26-teacher-launch-hardening.md
 * Task 4). Wraps every registry page route in routeFor (src/App.tsx) so a
 * route's `gate` is enforced at the router level, not only by whichever
 * pages remembered to self-guard. Denied → redirect to /me (the one page
 * every authenticated user can reach). While the gate is still resolving,
 * render the same PageLoader App.tsx uses for lazy-route Suspense — never
 * flash gated content before the redirect lands.
 * Existing page-level guards (LeadershipKPI, GamesConsole, ...) are additive
 * on top of this, not replaced by it.
 *
 * 'public' routes (/widget/*, /kiosk, /apply, ...) short-circuit here, before
 * useRouteGate (and the auth queries it calls, e.g. useCurrentUserStaff) is
 * ever invoked — an unauthenticated visitor on those routes shouldn't kick
 * off queries that assume a logged-in user. This is a plain early return in
 * RouteGateGuard itself (which calls no hooks), not a conditional hook call:
 * the actual gate evaluation is delegated to RouteGateGuardResolved below, so
 * rules-of-hooks stays intact.
 */
export function RouteGateGuard({
  gate,
  routePath,
  children,
}: {
  gate: RouteGate;
  /** The registry path pattern (e.g. "/grow/knowledge/:slug") — matched
   * against LOCATION_VIEWER_ALLOWED_PATHS verbatim, never the live resolved
   * URL. Required so the location-viewer allowlist (Task 3.1) can enforce
   * fail-closed even on 'public'/'authenticated' gates that would otherwise
   * always pass. */
  routePath: string;
  children: JSX.Element;
}): JSX.Element | null {
  if (gate === "public") return children;
  return (
    <RouteGateGuardResolved gate={gate} routePath={routePath}>
      {children}
    </RouteGateGuardResolved>
  );
}

function RouteGateGuardResolved({
  gate,
  routePath,
  children,
}: {
  gate: RouteGate;
  routePath: string;
  children: JSX.Element;
}): JSX.Element | null {
  const { allowed, isLoading } = useRouteGate(gate);
  // Read the staff role directly (not from useRouteGate, which omits it for
  // 'authenticated'/'privileged'/owner/... gates) so the accountant allowlist
  // applies to every guarded route regardless of gate type.
  const { currentUserStaff, isLoading: staffLoading } = useCurrentUserStaff();
  const { isLocationViewer, isLoading: locationSessionLoading } = useLocationSession();
  const { isRestricted, isLoading: restrictedLoading } = useRestrictedStaff();

  // Location-viewer allowlist takes precedence over the route's own `gate`
  // (every allowlisted route happens to be 'authenticated' today, but this
  // is a hard exclusive gate, not layered on top — see
  // docs/plans/2026-07-30-location-login.md Task 3.1). Fail closed: an
  // unknown/future route is never in the allowlist, so it's blocked.
  if (isLocationViewer || locationSessionLoading) {
    if (locationSessionLoading) return <PageLoader />;
    if (!isLocationAllowedRoutePath(routePath)) return <Navigate to={LOCATION_VIEWER_HOME_PATH} replace />;
    return children;
  }

  // Restricted-staff allowlist (insider-threat containment, see
  // src/routes/restrictedAllowlist.ts) — same exclusive-gate precedence as
  // the location-viewer check above, evaluated next so a restricted account
  // never falls through to the route's own `gate`. RLS
  // (supabase/migrations/20260810130000_restricted_staff_lockdown.sql) is
  // the real boundary; this only controls what renders.
  if (isRestricted || restrictedLoading) {
    if (restrictedLoading) return <PageLoader />;
    if (!isRestrictedAllowedRoutePath(routePath)) return <Navigate to={RESTRICTED_STAFF_HOME_PATH} replace />;
    return children;
  }

  if (isLoading || staffLoading) return <PageLoader />;
  // Accountant allowlist (src/routes/accountantAllowlist.ts): exclusive on
  // top of the route's own gate — an unlisted path never renders, even when
  // a wide gate ('authenticated'/'privileged') would pass.
  if (currentUserStaff?.role === "accountant") {
    if (!allowed || !isAccountantAllowedRoutePath(routePath)) {
      return <Navigate to={ACCOUNTANT_HOME_PATH} replace />;
    }
    return children;
  }
  if (!allowed) return <Navigate to="/me" replace />;
  return children;
}
